import React, { useState, useMemo, useEffect } from 'react';
import { Checkbox } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChannelListItem from 'Components/Units/channel-list-item/channel-list-item.component';
import { filterArray } from 'utils/functions';

const UnitListItem = ({ unit, selectedChannels, disabledChannels, search, defaultShow, setValues, globalSelectAll, setInitialValues }) => {
    const { nameLocalized, channels } = unit;
    const unitEnabled = unit.isEnabled !== false;
    const [show, setShow] = useState(defaultShow);
    const [selectAll, setSelectAll] = useState(false);
    const isSearching = Boolean(search && search !== '');

    const filteredChannels = useMemo(() => {
        if (isSearching)
            return filterArray(channels, search, 'nameLocalized');
        return channels;
    }, [channels, search, isSearching]);


    const selectedChannelCount = useMemo(() => {
        return selectedChannels ? selectedChannels.filter(ch => channels.some(channel => channel.id === ch.id)).length : 0;
    }, [selectedChannels, channels]);

    useEffect(() => {
        if (!unitEnabled) {
            setSelectAll(false);
        } else {
            setSelectAll(globalSelectAll);
        }
    }, [globalSelectAll, unitEnabled]);

    const handleSelectAllChange = (e) => {
        if (!unitEnabled) return;
        const checked = e.target.checked;
        setSelectAll(checked);
        const updatedChannels = filteredChannels.filter(channel =>
            !selectedChannels.some(selectedChannel => selectedChannel.id === channel.id)
        ).map(channel => ({
            ...channel,
            unit: channel.unit
        }));

        setValues(prevValues => {
            const updatedValues = checked
                ? [...prevValues, ...updatedChannels]
                : prevValues.filter(channel => !filteredChannels.some(fc => fc.id === channel.id));
            return updatedValues;
        });
        setInitialValues(prevValues => {
            const updatedValues = checked
                ? [...prevValues, ...updatedChannels]
                : prevValues.filter(channel => !filteredChannels.some(fc => fc.id === channel.id));
            return updatedValues;
        });
    };

    // While searching, groups without matches are hidden (not unmounted) and the rest are expanded
    const isHidden = isSearching && !filteredChannels.length;
    const isOpen = show || isSearching;
    const isPartlySelected = !selectAll && selectedChannelCount > 0 && selectedChannelCount < channels.length;

    return (
        <div
            className={`channelGroup${!unitEnabled ? ' channelGroup--disabled' : ''}`}
            style={isHidden ? { display: 'none' } : undefined}
        >
            <div className="channelGroupHead" onClick={() => setShow(prev => !prev)}>
                <ExpandMoreIcon className={`channelGroupChevron${isOpen ? ' channelGroupChevron--open' : ''}`} />
                <span className="channelGroupName" title={nameLocalized}>{nameLocalized}</span>
                <span className="channelTableCount">{selectedChannelCount} / {channels.length}</span>
                <Checkbox
                    size="small"
                    color="default"
                    checked={selectAll}
                    indeterminate={isPartlySelected}
                    disabled={!unitEnabled}
                    onChange={handleSelectAllChange}
                    onClick={e => e.stopPropagation()}
                    id="unitListItemBtn-selectAll"
                    className="channelGroupSelectAll"
                />
            </div>
            {/* Kept mounted while collapsed: rows hold their selected unit of measure */}
            <div className="channelGroupBody" style={{ display: isOpen ? 'block' : 'none' }}>
                {filteredChannels.map(channel => (
                    <ChannelListItem
                        key={channel.id}
                        channel={channel}
                        isChecked={selectAll || (selectedChannels && selectedChannels.some(ch => ch.id === channel.id))}
                        unit={(selectedChannels && selectedChannels.some(ch => ch.id === channel.id))
                            ? selectedChannels.find(ch => ch.id === channel.id).unit
                            : channel.unit}
                        isDisabled={
                            !unitEnabled ||
                            (disabledChannels && disabledChannels.includes(channel.id))
                        }
                        setValues={setValues}
                        selectAll={selectAll}
                        setInitialValues={setInitialValues}
                    />
                ))}
            </div>
        </div>
    );
};

export default UnitListItem;
